import { isSome } from '@crocodille/util';
import { Listbox } from '@headlessui/react';
import cn from 'classnames';
import {
  Children,
  FocusEventHandler,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from 'react';

import inputStyles from '../input/input.module.scss';
import styles from './select.module.scss';

import type { FormField } from '@crocodille/common';
import IconChevronDown from '@icons/chevron-down';

export interface SelectOptionProps<V> {
  readonly value: V;
  readonly label: string;
}

export function SelectOption<V>(props: SelectOptionProps<V>) {
  return (
    <Listbox.Option className={styles.option} value={props.value}>
      {props.label}
    </Listbox.Option>
  );
}

type Child<V> = React.ReactElement<SelectOptionProps<V>, typeof SelectOption>;

function isSelectChild<V>(child: React.ReactNode): child is Child<V> {
  return isValidElement(child) && child.type === SelectOption;
}

export interface SelectProps<V> {
  readonly value?: V;
  onChange?: (value: V) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  onFocus?: FocusEventHandler<HTMLDivElement>;
  readonly label: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly invalid?: boolean;
  readonly className?: string;
  readonly name?: string;
}

type FieldProps = 'value' | 'onChange' | 'disabled' | 'onBlur' | 'required';

export function fieldToSelectProps<V>(
  field: Pick<FormField<V>, 'error' | FieldProps>,
) {
  return {
    invalid: isSome(field.error),
    value: field.value,
    onChange: field.onChange,
    onBlur: field.onBlur,
    disabled: field.disabled,
    required: field.required,
  } satisfies Pick<SelectProps<V>, 'invalid' | FieldProps>;
}

function Select<V>(props: React.PropsWithChildren<SelectProps<V>>) {
  const {
    children,
    onChange,
    onFocus,
    onBlur,
    disabled,
    required,
    invalid,
    label,
    className,
  } = props;

  const [innerValue, setInnerValue] = useState<V>(props.value as V);
  const value = props.value === undefined ? innerValue : props.value;

  const handleValueChange = useCallback(
    (value: V) => {
      setInnerValue(value);
      onChange?.(value);
    },
    [onChange],
  );

  const selectedChild = useMemo(() => {
    let selected: Child<V> | null = null;

    Children.forEach(children ?? [], (child) => {
      if (isSelectChild<V>(child) && child.props.value === value) {
        selected = child;
      }
    });

    return selected as Child<V> | null;
  }, [value, children]);

  const [focused, setFocused] = useState(false);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  return (
    <Listbox
      as="div"
      className={cn(
        inputStyles.container,
        {
          [inputStyles.isFilled]:
            isSome(value) && (typeof value !== 'string' || value.length > 0),
          [inputStyles.isFocused]: focused,
          [inputStyles.isRequired]: required,
          [inputStyles.isInvalid]: invalid,
        },
        className,
      )}
      value={value}
      onChange={handleValueChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      name={props.name}
    >
      {({ open }) => (
        <>
          <Listbox.Label className={inputStyles.label}>{label}</Listbox.Label>
          <Listbox.Button
            className={cn(inputStyles.input, styles.button, {
              [styles.isOpen]: open,
            })}
          >
            {selectedChild?.props?.label}
            <div className={styles.dropIconContainer}>
              <IconChevronDown className={styles.dropIcon} />
            </div>
          </Listbox.Button>
          <Listbox.Options className={styles.options}>
            {children}
          </Listbox.Options>{' '}
        </>
      )}
    </Listbox>
  );
}

export default Select;
